import React from 'react';
import { connect } from 'react-redux';
import SubSidebar from 'components/SubSidebar';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classNames from 'classnames';
import { logout } from 'actions';
import _ from 'lodash';
import * as clevertapZendeskLinks from 'clevertap/zendeskLinks';
import { showUpgradeModal } from 'reducers/featureRestrictions/actions';
import { isFullSiteMode } from 'utils/fullSite';
import * as clevertapSidebar from 'clevertap/sidebar';
import withFeatureFlags from 'hocs/withFeatureFlags';
import isMokaposUser from 'selectors/isMokaposUser';
import * as clevertapSidebarFtue from 'clevertap/sidebarFtue';
import { fetchExperiences, completeExperience } from 'reducers/ftue/actions';
import {
  FTUE_ONBOARDING_MERCHANT,
  FTUE_PAYMENT_CONFIG_SIDEBAR,
  FTUE_TAX_CONFIG_TOUR,
  FTUE_PAYMENT_CONFIG_PAGE_TOUR,
  FTUE_ONLINE_STORE_GO_STORE_RED_DOT,
  FTUE_ONLINE_STORE_FACEBOOK_RED_DOT,
  FTUE_ONLINE_STORE_GOOGLE_RED_DOT,
} from 'reducers/ftue/ftueKeys';
import isExperienceComplete from 'selectors/isExperienceComplete';
import {
  ALLOWED_BUSINESS_CATEGORY_ID,
  ALLOWED_EMAIL,
} from 'utils/onlineStoreUtils';
import SidebarAccountInfoDropdownItem from './SidebarAccountInfoDropdownItem';
import SidebarFooter from './SidebarFooter';
import SidebarMokaLogo from './SidebarMokaLogo';
import SidebarFtue from '../SidebarFtue';
import SidebarFooterFtue from '../SidebarFtue/SidebarFooterFtue';
import SidebarPayconFtue from './SidebarPayconFtue';
import styles from './style.css';

function parseActiveSidebarFromPath(path) {
  const chunks = path.split('/');

  function isAccountSettingSidebar(chunks) {
    return (
      (chunks.length && chunks[1] === 'settings') || chunks[1] === 'outlets'
    );
  }

  const deduceSelectedNavFromUrl = path.split('/')[1].replace(/-/g, ' ');

  return isAccountSettingSidebar(chunks)
    ? 'account settings'
    : deduceSelectedNavFromUrl;
}

/**
 * redDotMenus is used to determine whether a menu should show red dot or not
 * the `shouldShowRedDot` field is a function that receive props and returns boolean
 * @type {{subMenu: string, shouldShowRedDot: Function, menu: string}[]}
 */
const redDotMenus = [
  {
    menu: 'reports',
    subMenu: 'Sales',
    shouldShowRedDot: props => {
      const {
        outlets,
        userPropertiesLoaded,
        userProperties: { ftueSalesSummaryOutletComparisonShown },
        outletComparisonEnabled,
        outletComparisonFtueEnabled,
      } = props;
      return (
        outlets &&
        outlets.length > 1 &&
        outletComparisonEnabled &&
        outletComparisonFtueEnabled &&
        userPropertiesLoaded &&
        !ftueSalesSummaryOutletComparisonShown
      );
    },
  },
  {
    menu: 'payments',
    subMenu: 'Payment Configuration',
    shouldShowRedDot: props => {
      return props.displayPayconRedDot;
    },
  },
  {
    menu: 'account settings',
    subMenu: 'Checkout',
    shouldShowRedDot: props => {
      return props.displayTaxconTour;
    },
  },
  {
    menu: 'online store',
    subMenu: 'GoStore',
    shouldShowRedDot: props => {
      return props.displayGoStoreRedDot;
    },
  },
  {
    menu: 'online store',
    subMenu: 'Facebook & Instagram Shop',
    shouldShowRedDot: props => {
      return props.displayFacebookRedDot;
    },
  },
  {
    menu: 'online store',
    subMenu: 'Google Shopping',
    shouldShowRedDot: props => {
      return props.displayGoogleRedDot;
    },
  },
];

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    const { location } = props;
    const path = location.pathname;

    this.state = {
      activeSidebarName: parseActiveSidebarFromPath(path),
      originalActiveSidebarName: '',
      isSidebarTourRunning: false,
    };
    this._setActiveSidebarName = this._setActiveSidebarName.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.pathname !== this.props.location.pathname) {
      this.setState({
        activeSidebarName: parseActiveSidebarFromPath(
          nextProps.location.pathname
        ),
      });
    }
  }

  componentDidMount() {
    const { useFtueApi, fetchExperiences } = this.props;
    if (useFtueApi) {
      fetchExperiences();
    }
  }

  render() {
    const {
      subMenuOf,
      user,
      onLockedItemClick,
      mokapayMode,
      displayTour,
      displayPayconTour,
      enableSidebarFtue,
      showSwitchBanner,
      showBillingReminder,
    } = this.props;
    const { isSidebarTourRunning, activeSidebarName } = this.state;
    const isSidebarPayconFtueRunning =
      displayPayconTour && activeSidebarName === 'payments';

    // These pages are not supported in Mobile devices requesting Desktop site
    const hiddenSidebarItems = isFullSiteMode() ? ['table management'] : [];

    const backOfficemenus = _.filter(subMenuOf, function(o) {
      return o[0].access === 'Back-office';
    });
    const menuHashes = _.map(backOfficemenus, bom => ({
      menu: bom[0].menu,
      priority_rank: bom[0].priority_rank,
      isLocked: mokapayMode && bom.every(m => !m.is_allowed_for_moka_pay),
    }));

    const sortedHashesMenu = _.sortBy(menuHashes, 'priority_rank');

    const sideBarMenus = sortedHashesMenu.map(function(menu) {
      const menuName = menu.menu;
      const lowerCasedMenuName = menuName.toLowerCase();

      // Don't render hidden item
      if (hiddenSidebarItems.includes(lowerCasedMenuName)) {
        return null;
      }

      if (
        lowerCasedMenuName === 'online store' &&
        !this._canVisitOnlineStore()
      ) {
        return null;
      }

      // Render locked item directly
      if (menu.isLocked) {
        return (
          <li key={menuName} className="disabled">
            <a className="has-icon" onClick={onLockedItemClick}>
              <span className="glyphicon glyphicon-lock" /> {menuName}
            </a>
          </li>
        );
      }

      // Render sidebar items depending on if it is a group or individual item
      if (subMenuOf[lowerCasedMenuName].length > 1) {
        return (
          subMenuOf[lowerCasedMenuName] &&
          this._validateFeatureFlagBusinessLib(lowerCasedMenuName) &&
          this._getSidebarWithSub(lowerCasedMenuName)
        );
      }
      return (
        subMenuOf[lowerCasedMenuName] &&
        this._getSidebarWithoutSub(lowerCasedMenuName)
      );
    }, this);

    return (
      <div
        className={classNames({
          'ftue-on': isSidebarTourRunning || isSidebarPayconFtueRunning,
        })}
      >
        {displayTour && (
          <SidebarFtue
            onTourStart={this.handleFtueTourStart}
            onTourEnd={this.handleFtueTourEnd}
            onEndStep={this.handleFtueEndStep}
            onEnterStep={this.handleFtueEnterStep}
            onWelcome={this.handleFtueWelcome}
            onCompletion={this.handleFtueComplete}
            onRestart={this.handleFtueRestart}
            onClickLanjut={this.handleClickLanjut}
            onClickKembali={this.handleClickKembali}
          />
        )}
        <nav
          className={classNames(
            'navbar',
            'navbar-inverse',
            'navbar-fixed-top',
            showSwitchBanner &&
              (showBillingReminder
                ? styles.navWithReminder
                : styles.navWithSwitchBanner)
          )}
          id="sidebar-wrapper"
          role="navigation"
        >
          <ul className="nav sidebar-nav">
            <SidebarMokaLogo
              mokapayMode={mokapayMode}
              link={this._mokaLogoLink()}
            />
            <SidebarAccountInfoDropdownItem
              user={user}
              onTriggerClick={() => this._setActiveSidebarName(null)}
              onLogoutClick={this._logout}
            />
            {sideBarMenus}
          </ul>
        </nav>
        {/* SidebarFooterFtue will replace SidebarFooter in the future */}
        {enableSidebarFtue ? (
          <SidebarFooterFtue
            onKnowledgeCenterLinkClick={this._onClickKnowledge}
          />
        ) : (
          <SidebarFooter onKnowledgeCenterLinkClick={this._onClickKnowledge} />
        )}
        {isSidebarPayconFtueRunning && (
          <SidebarPayconFtue onTourEnd={this.handlePayconTourEnd} />
        )}
      </div>
    );
  }

  handlePayconTourEnd = () => {
    const { completeExperience } = this.props;
    completeExperience(FTUE_PAYMENT_CONFIG_SIDEBAR);
  };

  /**
   * Right before the tour start, we need to store the original active item,
   * so that we can go back after the tour
   */
  handleFtueTourStart = () => {
    const { activeSidebarName } = this.state;
    const { user } = this.props;
    this.setState({
      isSidebarTourRunning: true,
      originalActiveSidebarName: activeSidebarName,
    });
    clevertapSidebarFtue.tourStart(user);
  };

  /**
   * Right after the tour, we need to clean up what we have changed.
   */
  handleFtueTourEnd = () => {
    const { originalActiveSidebarName } = this.state;
    const { user } = this.props;
    this.setState({
      isSidebarTourRunning: false,
      activeSidebarName: originalActiveSidebarName,
    });
    clevertapSidebarFtue.tourEnd(user);
  };

  /**
   * This is triggered right before a step is displayed. For some item we need
   * to expand the menu, this is the place to do it.
   */
  handleFtueEnterStep = step => {
    const { openSidebarItem, trackEvent } = step.ftueOptions;
    const { user } = this.props;
    this.setState({
      activeSidebarName: openSidebarItem,
    });
    trackEvent.default(user);
  };

  /**
   * And this is to complement `handleFtueEnterStep`, we need to close it first
   * before we open a new one. Otherwise the spotlight and tooltip will misalign
   */
  handleFtueEndStep = () => {
    const { originalActiveSidebarName } = this.state;
    this.setState({
      activeSidebarName: originalActiveSidebarName,
    });
  };

  /**
   * When welcome modal shows up, before the tour starts.
   * Send clevertap event `Welcome`
   */
  handleFtueWelcome = () => {
    const { user } = this.props;
    clevertapSidebarFtue.welcome(user);
  };

  /**
   * Triggered when the tour is complete.
   */
  handleFtueComplete = () => {
    const { user, useFtueApi, completeExperience } = this.props;
    clevertapSidebarFtue.tourComplete(user);
    if (useFtueApi) {
      completeExperience(FTUE_ONBOARDING_MERCHANT);
    }
  };

  /**
   * This is triggered when the tour restart.
   */
  handleFtueRestart = () => {
    const { user } = this.props;
    clevertapSidebarFtue.tourRestart(user);
  };

  /**
   * This is when user continue to the next step.
   * @param step
   */
  handleClickLanjut = step => {
    const { trackEvent } = step.ftueOptions;
    const { user } = this.props;
    trackEvent.next(user);
  };

  /**
   * Triggered when user back to the previous step.
   * @param step
   */
  handleClickKembali = step => {
    const { trackEvent } = step.ftueOptions;
    const { user } = this.props;
    trackEvent.back(user);
  };

  _validateFeatureFlagBusinessLib = name => {
    const { businessLibraryMenu, isEligibleBusinessLib } = this.props;
    if (name === 'business library') {
      return businessLibraryMenu && isEligibleBusinessLib;
    }

    return true;
  };

  _onClickKnowledge = () => {
    clevertapZendeskLinks.knowledgeCenterStart();
  };

  _mokaLogoLink = () => {
    const { user, mokapayMode } = this.props;
    if (user) {
      if (user.is_owner || user.is_manager) {
        if (mokapayMode) {
          return '/reports/sales/summary';
        }
        return '/dashboard';
      }
      return '/settings/accounts';
    }
    // FIXME This branch should be dead code
    return '/login';
  };

  // this function name should be 'onclick sidebar menu'
  _setActiveSidebarName = name => {
    clevertapSidebar.clickSidebar({ menu: name });

    this.setState({
      activeSidebarName: name,
    });
  };

  _getSidebarWithSub = name => {
    const {
      subscriptionExpired,
      location,
      subMenuOf,
      user,
      mokapayMode,
      onLockedItemClick,
    } = this.props;
    const urls = [];
    subMenuOf[name].forEach(function(menu) {
      menu.urls.forEach(function(url) {
        urls.push(url);
      });
    });
    const path = location.pathname;
    const isItemOpened = name === this.state.activeSidebarName;
    const redDotSubMenus = redDotMenus
      .filter(m => m.menu === name && m.shouldShowRedDot(this.props))
      .map(m => m.subMenu);
    const itemClassName = classNames('dropdown', { open: isItemOpened }, name);
    const linkClassName = classNames('dropdown-toggle', 'has-icon', {
      active: urls.includes(path),
    });

    return (
      <li key={name} className={itemClassName}>
        <a
          className={linkClassName}
          onClick={() => {
            this._setActiveSidebarName(isItemOpened ? '' : name);
          }}
        >
          <span className="glyphicon glyphicon-chevron-right" /> {name}
          {!isItemOpened && redDotSubMenus.length > 0 && (
            <span className="menu-red-dot" />
          )}
        </a>
        <SubSidebar
          links={this._toLinks(subMenuOf[name])}
          location={location}
          user={user}
          subscriptionExpired={subscriptionExpired}
          mokapayMode={mokapayMode}
          onLockedItemClick={onLockedItemClick}
          redDotSubMenus={redDotSubMenus}
          canVisitGoogleOnlineStore={this._canVisitGoogleOnlineStore()}
          canVisitFacebookOnlineStore={this._canVisitFacebookOnlineStore()}
        />
      </li>
    );
  };

  _getSidebarWithoutSub = name => {
    const { location, subMenuOf } = this.props;
    const path = location.pathname;
    const firstSubSidebar = this._getFirstSubSidebar(subMenuOf[name]);
    const linkClassName = classNames({
      active: path.startsWith(`/${name}`) || path === '/',
    });

    return (
      <li
        key={name}
        className={name}
        onClick={() => this._setActiveSidebarName(name)}
      >
        <Link className={linkClassName} to={firstSubSidebar}>
          {name}
        </Link>
      </li>
    );
  };

  _getFirstSubSidebar = permissions => {
    return _.orderBy(permissions, 'priority_rank')[0].urls[0];
  };

  _logout = e => {
    e.preventDefault();
    e.stopPropagation();
    this.props.logout();
  };

  _toLinks = subMenus => {
    const links = [];
    const sortedSubMenu = _.orderBy(subMenus, 'priority_rank');
    for (let i = 0; i < sortedSubMenu.length; i++) {
      const subMenu = sortedSubMenu[i];
      links.push({
        href: subMenu.urls[0],
        label: subMenu.sub_menu,
        urls: subMenu.urls,
        isLocked: !subMenu.is_allowed_for_moka_pay,
      });
    }
    return links;
  };

  _canVisitOnlineStore = () => {
    const { enableShoppingExt } = this.props;

    return (
      (this._isOnlineStoreEmailAllowed() &&
        this._isOnlineStoreBusinessCategoryAllowed()) ||
      (enableShoppingExt && this._isOnlineStoreBusinessCategoryAllowed())
    );
  };

  _canVisitGoogleOnlineStore = () => {
    const { enableGoogleShoppingExt } = this.props;

    return (
      (this._isOnlineStoreEmailAllowed() &&
        this._isOnlineStoreBusinessCategoryAllowed()) ||
      (enableGoogleShoppingExt && this._isOnlineStoreBusinessCategoryAllowed)
    );
  };

  _canVisitFacebookOnlineStore = () => {
    const { enableFacebookShoppingExt } = this.props;

    return (
      (this._isOnlineStoreEmailAllowed() &&
        this._isOnlineStoreBusinessCategoryAllowed()) ||
      (enableFacebookShoppingExt &&
        this._isOnlineStoreBusinessCategoryAllowed())
    );
  };

  _isOnlineStoreEmailAllowed = () => {
    const { user } = this.props;
    const { email } = user;

    return ALLOWED_EMAIL.some(e => e === email);
  };

  _isOnlineStoreBusinessCategoryAllowed = () => {
    const { user } = this.props;
    const {
      business: { business_category_id: businessCategoryId },
    } = user;

    return ALLOWED_BUSINESS_CATEGORY_ID.some(id => id === businessCategoryId);
  };
}

Sidebar.propTypes = {
  loggedIn: PropTypes.bool,
  currentlySending: PropTypes.bool,
  dispatch: PropTypes.func,
  outlets: PropTypes.array,
  onLockedItemClick: PropTypes.func,
  mokapayMode: PropTypes.bool,
  outletComparisonEnabled: PropTypes.bool,
  outletComparisonFtueEnabled: PropTypes.bool,
  userPropertiesLoaded: PropTypes.bool,
  userProperties: PropTypes.object,
  displayTour: PropTypes.bool,
  displayPayconTour: PropTypes.bool,
  displayTaxconTour: PropTypes.bool,
};

Sidebar.defaultProps = {
  mokapayMode: false,
  outletComparisonEnabled: false,
  outletComparisonFtueEnabled: false,
  displayTour: false,
  displayPayconTour: false,
  displayTaxconTour: false,
  onLockedItemClick: () => {},
};

const mapStateToProps = (state, ownProps) => {
  const {
    enableSidebarFtue,
    useFtueApi,
    enablePaymentConfiguration,
    enableShoppingExt,
    businessLibraryMenu,
    enableTaxConfiguration,
    ecommerceAttributes,
  } = ownProps;
  const { permissions, user } = state.moka;
  const { expired } = state.subscriptionExpired;
  const { listInventoryFeature } = state.inventoryFeatures;
  const isEligibleBusinessLib = listInventoryFeature.includes(
    'business-library-menu'
  );
  const isEligibleRetail = listInventoryFeature.includes(
    'ECOMMERCE_ATTRIBUTES'
  );

  // A very strange way to generate sidebar items
  const subMenuOf = _.groupBy(permissions, permission =>
    permission.menu.toLowerCase()
  );

  // Hide "Payments" menu if not ready yet
  if (!enablePaymentConfiguration) {
    delete subMenuOf.payments;
  }

  // Hide Promo if toggle is on
  if (businessLibraryMenu && isEligibleBusinessLib) {
    subMenuOf['outlet library'] = subMenuOf['outlet library'].filter(
      subMenu => {
        return subMenu.sub_menu !== 'Promo';
      }
    );
  }

  // Hide Brand Inventory if toggle is on
  if (!ecommerceAttributes || !isEligibleRetail) {
    if (subMenuOf.library) {
      subMenuOf.library = subMenuOf.library.filter(subMenu => {
        return subMenu.sub_menu !== 'Brands';
      });
    }
    if (subMenuOf['outlet library']) {
      subMenuOf['outlet library'] = subMenuOf['outlet library'].filter(
        subMenu => {
          return subMenu.sub_menu !== 'Brands';
        }
      );
    }
  }

  // Assume user has completed unless API tells us it is not
  let hasUserCompletedTour = true;
  if (useFtueApi) {
    hasUserCompletedTour = isExperienceComplete(
      state,
      FTUE_ONBOARDING_MERCHANT
    );
  }
  const isOwnerUser = user.is_owner;

  return {
    subMenuOf,
    user,
    subscriptionExpired: state.subscriptionExpired,
    outlets: user.outlets,
    mokapayMode: !isMokaposUser(state),
    userPropertiesLoaded: state.userProperties.loaded,
    userProperties: state.userProperties.props,
    billingNotification: state.billingNotification,
    isEligibleBusinessLib,
    displayTour:
      enableSidebarFtue &&
      !hasUserCompletedTour &&
      isOwnerUser &&
      isMokaposUser(state) &&
      !expired,
    useFtueApi,
    displayPayconRedDot:
      enablePaymentConfiguration &&
      !isExperienceComplete(state, FTUE_PAYMENT_CONFIG_PAGE_TOUR),
    displayPayconTour:
      enablePaymentConfiguration &&
      !isExperienceComplete(state, FTUE_PAYMENT_CONFIG_SIDEBAR),
    displayTaxconTour:
      enableTaxConfiguration &&
      !isExperienceComplete(state, FTUE_TAX_CONFIG_TOUR),
    displayGoStoreRedDot:
      enableShoppingExt &&
      !isExperienceComplete(state, FTUE_ONLINE_STORE_GO_STORE_RED_DOT),
    displayFacebookRedDot:
      enableShoppingExt &&
      !isExperienceComplete(state, FTUE_ONLINE_STORE_FACEBOOK_RED_DOT),
    displayGoogleRedDot:
      enableShoppingExt &&
      !isExperienceComplete(state, FTUE_ONLINE_STORE_GOOGLE_RED_DOT),
  };
};

const mapDispatchToProps = {
  onLockedItemClick: showUpgradeModal,
  logout,
  fetchExperiences,
  completeExperience,
};

const connected = connect(
  mapStateToProps,
  mapDispatchToProps
)(Sidebar);

const mapFlagsToProps = flags => ({
  outletComparisonEnabled: flags.summaryPerOutlet,
  outletComparisonFtueEnabled: flags.summaryPerOutletFtue,
  businessLibraryMenu: flags.businessLibraryMenu,
  enableSidebarFtue: flags.enableSidebarFtue,
  useFtueApi: flags.useFtueApi,
  enablePaymentConfiguration: flags.enablePaymentConfiguration,
  enableTaxConfiguration: flags.enableTaxConfiguration,
  ecommerceAttributes: flags.ecommerceAttributes,
  enableShoppingExt: flags.enableShoppingExt,
  enableGoogleShoppingExt: flags.enableGoogleShoppingExt,
  enableFacebookShoppingExt: flags.enableFacebookShoppingExt,
});

export default withFeatureFlags(mapFlagsToProps)(connected);
